/* eslint-disable react/prop-types */
// prop-types disabled: the tiles/lists here are internal to this file only.
import { useMemo } from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingContainer from 'components/common/LoadingContainer';
import SoftBadge from 'components/common/SoftBadge';
import { formatMoney, parseRouteParams } from 'helpers/utils';
import paths from 'routes/paths';
import { usePledges } from 'hooks/usePledges';
import {
  PLEDGE_STATUSES,
  PLEDGE_FREQUENCIES,
  CONTACT_METHODS,
  pledgeRef,
  donorFullName
} from './pledgeConstants';

// Statuses that still need attention (not terminal, not paused)
const OPEN_STATUSES = [
  'new',
  'awaiting_follow_up',
  'payment_scheduled',
  'active',
  'partially_paid'
];

const StatTile = ({ label, value, color = 'primary', subtitle }) => (
  <Card className="h-100">
    <Card.Body className="py-3">
      <p className="text-600 fs--2 mb-1 text-nowrap">{label}</p>
      <h4 className={`mb-0 text-${color}`}>{value}</h4>
      {subtitle && <p className="text-500 fs--2 mb-0 mt-1">{subtitle}</p>}
    </Card.Body>
  </Card>
);

const PledgeListCard = ({ icon, title, emptyText, color, children, count }) => (
  <Card className="h-100">
    <Card.Header className="py-2 bg-light">
      <h6 className="mb-0">
        <FontAwesomeIcon icon={icon} className={`me-2 text-${color}`} />
        {title}
        {count > 0 && (
          <SoftBadge pill bg={color} className="ms-2">
            {count}
          </SoftBadge>
        )}
      </h6>
    </Card.Header>
    <Card.Body className="p-0">
      {count === 0 ? (
        <p className="text-600 text-center py-4 mb-0 fs--1">{emptyText}</p>
      ) : (
        children
      )}
    </Card.Body>
  </Card>
);

const PledgeRows = ({ pledges, dateField, dateLabel }) => (
  <Table responsive size="sm" className="mb-0 fs--1">
    <thead>
      <tr>
        <th>Pledge</th>
        <th>Donor</th>
        <th>Amount</th>
        <th>{dateLabel}</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      {pledges.map(pledge => {
        const status = PLEDGE_STATUSES[pledge.effectiveStatus] || {};
        return (
          <tr key={pledge.id}>
            <td>
              <Link
                to={parseRouteParams(paths.pledgeProfile, { id: pledge.id })}
                className="fw-semi-bold"
              >
                {pledgeRef(pledge)}
              </Link>
            </td>
            <td>{donorFullName(pledge.donor)}</td>
            <td>
              {formatMoney(Number(pledge.amount))}
              {pledge.frequency !== 'one_off' && (
                <span className="text-500">
                  /{pledge.frequency === 'annually' ? 'yr' : 'mo'}
                </span>
              )}
            </td>
            <td>
              {pledge[dateField]
                ? dayjs(pledge[dateField]).format('DD/MM/YYYY')
                : '—'}
            </td>
            <td>
              <SoftBadge pill bg={status.bg || 'secondary'}>
                {status.label || pledge.effectiveStatus}
              </SoftBadge>
            </td>
          </tr>
        );
      })}
    </tbody>
  </Table>
);

const PledgeDashboard = () => {
  const navigate = useNavigate();
  const { pledges, isLoading } = usePledges();

  const stats = useMemo(() => {
    const today = dayjs().startOf('day');
    const weekAhead = today.add(7, 'day');

    const open = pledges.filter(p => OPEN_STATUSES.includes(p.effectiveStatus));

    const needsFollowUp = open.filter(p =>
      ['new', 'awaiting_follow_up'].includes(p.effectiveStatus)
    );
    const followUpsToday = needsFollowUp.filter(
      p => p.follow_up_date && dayjs(p.follow_up_date).isSame(today, 'day')
    );
    const overdueFollowUps = needsFollowUp.filter(
      p => p.follow_up_date && dayjs(p.follow_up_date).isBefore(today, 'day')
    );

    // Payment dates come from auto/scheduled pledges (next_due_date). Contact
    // pledges surface through follow-ups instead.
    const upcomingPayments = open.filter(
      p =>
        p.next_due_date &&
        !dayjs(p.next_due_date).isBefore(today, 'day') &&
        dayjs(p.next_due_date).isBefore(weekAhead, 'day')
    );
    const overduePayments = open.filter(
      p => p.next_due_date && dayjs(p.next_due_date).isBefore(today, 'day')
    );

    const dueThisWeek = open.filter(p => {
      const dates = [p.follow_up_date, p.next_due_date].filter(Boolean);
      return dates.some(d => {
        const date = dayjs(d);
        return !date.isBefore(today, 'day') && date.isBefore(weekAhead, 'day');
      });
    });

    const overdue = [...overdueFollowUps, ...overduePayments].filter(
      (p, i, arr) => arr.findIndex(x => x.id === p.id) === i
    );

    const notCancelled = pledges.filter(p => p.effectiveStatus !== 'cancelled');
    const totalPledged = notCancelled.reduce(
      (sum, p) => sum + (p.total_value ? Number(p.total_value) : 0),
      0
    );
    const openEndedCount = notCancelled.filter(
      p => !p.total_value && p.duration_type === 'until_cancelled'
    ).length;
    const totalCollected = pledges.reduce((sum, p) => sum + p.paidTotal, 0);
    const totalOutstanding = notCancelled.reduce(
      (sum, p) => sum + (p.outstanding || 0),
      0
    );

    const recent = [...pledges]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);

    return {
      total: pledges.length,
      active: open.length,
      awaitingFollowUp: needsFollowUp.length,
      dueThisWeek: dueThisWeek.length,
      overdue,
      followUpsToday,
      upcomingPayments,
      recent,
      totalPledged,
      openEndedCount,
      totalCollected,
      totalOutstanding
    };
  }, [pledges]);

  if (isLoading) return <LoadingContainer />;

  return (
    <>
      {/* Header + quick action */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Flex justifyContent="between" alignItems="center">
            <h4 className="mb-0">Pledge Dashboard</h4>
            <IconButton
              icon="plus"
              variant="primary"
              size="sm"
              onClick={() => navigate(paths.addPledge)}
            >
              Add New Pledge
            </IconButton>
          </Flex>
        </Card.Body>
      </Card>

      {/* Summary cards */}
      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <StatTile label="Total Pledges" value={stats.total} color="primary" />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Active Pledges"
            value={stats.active}
            color="success"
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Awaiting Follow-Up"
            value={stats.awaitingFollowUp}
            color="warning"
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Due This Week"
            value={stats.dueThisWeek}
            color="info"
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Overdue"
            value={stats.overdue.length}
            color={stats.overdue.length > 0 ? 'danger' : 'success'}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Total Pledged"
            value={formatMoney(stats.totalPledged)}
            color="primary"
            subtitle={
              stats.openEndedCount > 0
                ? `+ ${stats.openEndedCount} open-ended`
                : undefined
            }
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Total Collected"
            value={formatMoney(stats.totalCollected)}
            color="success"
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label="Outstanding"
            value={formatMoney(stats.totalOutstanding)}
            color="warning"
          />
        </Col>
      </Row>

      {/* Work lists */}
      <Row className="g-3 mb-3">
        <Col lg={6}>
          <PledgeListCard
            icon="bell"
            title="Follow-Ups Due Today"
            color="warning"
            emptyText="No follow-ups due today 🎉"
            count={stats.followUpsToday.length}
          >
            <Table responsive size="sm" className="mb-0 fs--1">
              <thead>
                <tr>
                  <th>Pledge</th>
                  <th>Donor</th>
                  <th>Contact</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {stats.followUpsToday.map(pledge => (
                  <tr key={pledge.id}>
                    <td>
                      <Link
                        to={parseRouteParams(paths.pledgeProfile, {
                          id: pledge.id
                        })}
                        className="fw-semi-bold"
                      >
                        {pledgeRef(pledge)}
                      </Link>
                    </td>
                    <td>{donorFullName(pledge.donor)}</td>
                    <td>
                      {CONTACT_METHODS[pledge.preferred_contact_method] || '—'}
                    </td>
                    <td>
                      {formatMoney(Number(pledge.amount))}
                      {pledge.frequency !== 'one_off' && (
                        <span className="text-500">
                          /{pledge.frequency === 'annually' ? 'yr' : 'mo'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PledgeListCard>
        </Col>
        <Col lg={6}>
          <PledgeListCard
            icon="clock"
            title="Upcoming Payments (7 days)"
            color="info"
            emptyText="No scheduled payments in the next 7 days"
            count={stats.upcomingPayments.length}
          >
            <PledgeRows
              pledges={stats.upcomingPayments}
              dateField="next_due_date"
              dateLabel="Due"
            />
          </PledgeListCard>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <PledgeListCard
            icon="exclamation-triangle"
            title="Overdue"
            color="danger"
            emptyText="Nothing overdue 🎉"
            count={stats.overdue.length}
          >
            <PledgeRows
              pledges={stats.overdue}
              dateField="follow_up_date"
              dateLabel="Was Due"
            />
          </PledgeListCard>
        </Col>
        <Col lg={6}>
          <PledgeListCard
            icon="plus-circle"
            title="Recent Pledges"
            color="primary"
            emptyText="No pledges yet — add the first one"
            count={stats.recent.length}
          >
            <Table responsive size="sm" className="mb-0 fs--1">
              <thead>
                <tr>
                  <th>Pledge</th>
                  <th>Donor</th>
                  <th>Frequency</th>
                  <th>Value</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map(pledge => (
                  <tr key={pledge.id}>
                    <td>
                      <Link
                        to={parseRouteParams(paths.pledgeProfile, {
                          id: pledge.id
                        })}
                        className="fw-semi-bold"
                      >
                        {pledgeRef(pledge)}
                      </Link>
                    </td>
                    <td>{donorFullName(pledge.donor)}</td>
                    <td>{PLEDGE_FREQUENCIES[pledge.frequency]}</td>
                    <td>
                      {pledge.total_value
                        ? formatMoney(Number(pledge.total_value))
                        : `${formatMoney(Number(pledge.amount))}/${
                            pledge.frequency === 'annually' ? 'yr' : 'mo'
                          }`}
                    </td>
                    <td>{dayjs(pledge.created_at).format('DD/MM/YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PledgeListCard>
        </Col>
      </Row>
    </>
  );
};

export default PledgeDashboard;
